import React, { useState, useMemo } from 'react';
import { colors, getAvatarColor, statusColors, ON_TIME_STYLE } from '../utils/colors';
import type { AdminUser, TeacherListItem, AttendanceStatus } from '../utils/types';

const TITLES = new Set([
  'mr', 'mrs', 'ms', 'miss', 'mx',
  'dr', 'prof', 'sir', 'madam', 'mam',
  'smt', 'shri', 'sri', 'shree',
]);

function initials(name: string) {
  const words = name
    .split(/\s+/)
    .map(w => w.replace(/\.$/, ''))
    .filter(w => w && !TITLES.has(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}


interface Props {
  adminUser: AdminUser | null;
  teachers: TeacherListItem[];
  onTeacherSelect: (teacher: TeacherListItem) => void;
  onLogout: () => void | Promise<void>;
}

type Group = 'unmarked' | 'absent' | 'late' | 'present';

const GROUP_ORDER: Group[] = ['unmarked', 'absent', 'late', 'present'];
const GROUP_LABEL: Record<Group, string> = {
  unmarked: 'NOT MARKED',
  absent:   'ABSENT',
  late:     'LATE',
  present:  'ON TIME',
};

interface StatusStyle {
  avatarBg: string;
  cardBg: string;
  cardBorder: string;
  pillBorder: string;
  pillText: string;
  letter: string;
}

function statusStyle(status: AttendanceStatus): StatusStyle {
  switch (status) {
    case 'present':
      return { avatarBg: ON_TIME_STYLE.text,        cardBg: ON_TIME_STYLE.bg,        cardBorder: ON_TIME_STYLE.border,        pillBorder: ON_TIME_STYLE.text,        pillText: ON_TIME_STYLE.text,        letter: 'P' };
    case 'absent':
      return { avatarBg: statusColors.absent.text,  cardBg: statusColors.absent.bg,  cardBorder: statusColors.absent.border,  pillBorder: statusColors.absent.text,  pillText: statusColors.absent.text,  letter: 'A' };
    case 'late':
      return { avatarBg: statusColors.late.text,    cardBg: statusColors.late.bg,    cardBorder: statusColors.late.border,    pillBorder: statusColors.late.text,    pillText: statusColors.late.text,    letter: 'L' };
    default:
      return { avatarBg: '#9CA3AF', cardBg: '#FFFFFF', cardBorder: '#E8E8E8', pillBorder: '#9CA3AF', pillText: '#9CA3AF', letter: '+' };
  }
}

const HomeScreen: React.FC<Props> = ({ adminUser, teachers, onTeacherSelect, onLogout }) => {
  const [search, setSearch] = useState('');
  const [showLogout, setShowLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleConfirmLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await onLogout();
    } finally {
      setLoggingOut(false);
      setShowLogout(false);
    }
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });

  const counts = useMemo(() => ({
    total:    teachers.length,
    present:  teachers.filter(t => t.todayStatus === 'present').length,
    absent:   teachers.filter(t => t.todayStatus === 'absent').length,
    late:     teachers.filter(t => t.todayStatus === 'late').length,
    unmarked: teachers.filter(t => !t.todayStatus).length,
  }), [teachers]);

  const pad2 = (n: number) => n.toString().padStart(2, '0');

  const filtered = useMemo(() => {
    if (!search) return teachers;
    const q = search.toLowerCase();
    return teachers.filter(t =>
      t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q)
    );
  }, [teachers, search]);

  const grouped = useMemo(() => {
    const groups: Record<Group, TeacherListItem[]> = { unmarked: [], absent: [], late: [], present: [] };
    for (const t of filtered) {
      const key: Group = t.todayStatus ?? 'unmarked';
      groups[key].push(t);
    }
    return groups;
  }, [filtered]);

  return (
    <div style={{
      height: '100dvh', background: '#F7F7F7',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* Top section: heading + date pill + status tiles */}
      <div style={{
        background: '#fff',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        paddingBottom: 14, paddingLeft: 16, paddingRight: 16,
        flexShrink: 0, borderBottom: '1px solid #EBEBEB',
      }}>
        {/* Heading + logout */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1C2024', lineHeight: 1.2 }}>Attendance</div>
          <button
            onClick={() => setShowLogout(true)}
            aria-label="Log out"
            style={{
              width: 38, height: 38, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fff', border: '1px solid #E8E8E8', borderRadius: 12,
              cursor: 'pointer', padding: 0,
            }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#1C2024" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        {/* Separator */}
        <div style={{ height: 1, background: '#EBEBEB', margin: '14px 0' }} />

        {/* Selected-section pill (date) */}
        <div style={{
          background: colors.primaryDark, color: '#fff',
          borderRadius: 14, padding: '12px 16px',
          fontSize: 14, fontWeight: 700, textAlign: 'center',
          letterSpacing: '0.2px',
        }}>
          {dateStr}
        </div>

        {/* Status tiles */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          {([
            { value: counts.total,                 label: 'TOTAL',   color: '#1C2024',              bg: '#ffffff',              border: '#000000' },
            { value: counts.present + counts.late, label: 'PRESENT', color: statusColors.present.text, bg: statusColors.present.bg, border: statusColors.present.border },
            { value: counts.present,               label: 'ONTIME',  color: ON_TIME_STYLE.text,        bg: ON_TIME_STYLE.bg,        border: ON_TIME_STYLE.border },
            { value: counts.late,                  label: 'LATE',    color: statusColors.late.text,    bg: statusColors.late.bg,    border: statusColors.late.border },
            { value: counts.absent,                label: 'ABSENT',  color: statusColors.absent.text,  bg: statusColors.absent.bg,  border: statusColors.absent.border },
          ]).map(t => (
            <div key={t.label} style={{
              flex: 1, background: t.bg,
              border: `1px solid ${t.border}`, borderRadius: 14,
              padding: '10px 6px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.color, lineHeight: 1.1 }}>
                {pad2(t.value)}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.color, letterSpacing: '0.6px', marginTop: 4, opacity: 0.85 }}>
                {t.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '14px 16px 8px', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          background: '#EFEFEF', borderRadius: 30,
          padding: '0 16px', gap: 10,
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#868F97" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search teachers..."
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 15, padding: '13px 0',
              background: 'transparent', color: '#1C2024',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 2 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#868F97" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Teacher list */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '6px 16px 24px',
        display: 'flex', flexDirection: 'column',
      }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textLight }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No teachers found</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Try a different search term</div>
          </div>
        ) : GROUP_ORDER.flatMap(group => {
          const items = grouped[group];
          if (items.length === 0) return [];
          return [
            <div
              key={`h-${group}`}
              style={{
                fontSize: 11, fontWeight: 800, color: '#868F97',
                letterSpacing: '0.8px', margin: '14px 4px 8px',
              }}
            >
              {GROUP_LABEL[group]}
            </div>,
            ...items.map(teacher => {
              const ss = statusStyle(teacher.todayStatus);
              const timeStr = (teacher.todayStatus === 'present' || teacher.todayStatus === 'late')
                ? (teacher.markedAt ?? '')
                : '';
              return (
                <button
                  key={teacher.id}
                  onClick={() => onTeacherSelect(teacher)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '10px 12px',
                    background: ss.cardBg,
                    border: `1.5px solid ${ss.cardBorder}`,
                    borderRadius: 16,
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                    marginBottom: 10,
                  }}
                >
                  {/* Profile / initials avatar */}
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: getAvatarColor(teacher.name),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, fontWeight: 800, color: '#fff',
                  }}>
                    {initials(teacher.name)}
                  </div>

                  {/* Name + meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 700, color: '#1C2024',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {teacher.name}
                    </div>
                    <div style={{
                      fontSize: 12, color: '#5D656C', marginTop: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      ID: {teacher.id}
                      {timeStr && (
                        <>
                          <span style={{ margin: '0 6px', color: '#C9CFD3' }}>•</span>
                          {timeStr}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status letter pill */}
                  <div style={{
                    width: 38, height: 36, borderRadius: 10,
                    border: `1.5px solid ${ss.pillBorder}`,
                    background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, fontWeight: 800, color: ss.pillText,
                    flexShrink: 0,
                  }}>
                    {ss.letter}
                  </div>
                </button>
              );
            }),
          ];
        })}
      </div>

      {/* Logout confirmation modal */}
      {showLogout && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: 24,
        }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 340 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 8 }}>Sign out?</div>
            <div style={{ fontSize: 14, color: colors.textMuted, marginBottom: 24 }}>
              {adminUser
                ? <>Signed in as <strong>{adminUser.name}</strong>. You'll need to log in again.</>
                : 'This will return to the login screen.'}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowLogout(false)}
                disabled={loggingOut}
                style={{ flex: 1, padding: '13px', background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: loggingOut ? 'not-allowed' : 'pointer', color: colors.text, opacity: loggingOut ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                disabled={loggingOut}
                style={{ flex: 1, padding: '13px', background: colors.danger, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: loggingOut ? 'not-allowed' : 'pointer', color: '#fff', opacity: loggingOut ? 0.7 : 1 }}
              >
                {loggingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
